import { Injector, type Type } from '@angular/core';
import { MatDialog, type MatDialogRef } from '@angular/material/dialog';
import type { Observable } from 'rxjs';

import type { ClarityDialogConfig } from '../models/clarity-dialog-config.model';
import type { ClarityDialogWidth } from '../models/clarity-dialog-width.type';
import { CLARITY_OVERLAY_DATA_TOKEN } from '../tokens/clarity-overlay-data.token';
import { closeMatDialogOnEscape } from '../utils/close-mat-dialog-on-escape.util';
import { CLARITY_DIALOG_PANEL_CLASS, CLARITY_MEDIA_DIALOG_PANEL_CLASS } from './clarity-dialog-panel-class.constant';
import { CLARITY_DIALOG_SIZE } from './clarity-dialog-size.constant';
import { ClarityPassiveDialog } from './clarity-passive-dialog';

/** Dismissal is on unless the caller says otherwise — the common case is a dialog a user may leave. */
const DEFAULT_DISMISSIBLE = true;

/**
 * A backdrop press leaves the dialog open unless the caller says otherwise. Only a Clarity fly-in closes on a
 * press outside it; a dialog holds a choice the reader has not made yet, and a stray click must not drop it.
 */
const DEFAULT_BACKDROP_DISMISSIBLE = false;

/** Back / Forward closes a dialog unless the caller says otherwise — Material's own default. */
const DEFAULT_CLOSE_ON_NAVIGATION = true;

/** The width every Clarity dialog opened at before the vocabulary existed. */
const DEFAULT_WIDTH: ClarityDialogWidth = 'default';

/**
 * The members of `MatDialogRef` this ref drives — and all that the `passive` backend,
 * `ClarityPassiveDialog`, has to provide for the two to run through one code path.
 */
type ClarityDialogHandle<R> = Pick<MatDialogRef<unknown, R>, 'afterClosed' | 'close' | 'disableClose'>;

/**
 * The CSS width a config asks for, with the library's default applied.
 *
 * A function rather than two `??` expressions: both backends resolve the same config, and a default
 * applied in two places is a default that can be changed in one.
 */
function resolveWidth(config: ClarityDialogConfig | undefined): string {
	return CLARITY_DIALOG_SIZE.widths[config?.width ?? DEFAULT_WIDTH];
}

/** The CSS width bound for the width a config asks for, with the library's default applied. */
function resolveMaxWidth(config: ClarityDialogConfig | undefined): string {
	return CLARITY_DIALOG_SIZE.maxWidths[config?.width ?? DEFAULT_WIDTH];
}

/** The CSS height bound for the width a config asks for, with the library's default applied. */
function resolveMaxHeight(config: ClarityDialogConfig | undefined): string {
	return CLARITY_DIALOG_SIZE.maxHeights[config?.width ?? DEFAULT_WIDTH];
}

/** The pane class each width adds beside `CLARITY_DIALOG_PANEL_CLASS`; `undefined` adds none. */
const WIDTH_PANEL_CLASS: Readonly<Record<ClarityDialogWidth, string | undefined>> = {
	default: undefined,
	wide: undefined,
	media: CLARITY_MEDIA_DIALOG_PANEL_CLASS,
};

/** The pane's classes: the library's own, its width's, then the caller's hook when it states one — for both backends. */
function resolvePanelClasses(config: ClarityDialogConfig | undefined): string[] {
	const ownClasses = [
		CLARITY_DIALOG_PANEL_CLASS,
		WIDTH_PANEL_CLASS[config?.width ?? DEFAULT_WIDTH],
		config?.panelClass,
	];

	return ownClasses.filter((panelClass): panelClass is string => panelClass !== undefined);
}

/** Ported from Clarity `apps/web/src/shared/ui/Modal.tsx` (commit `f9217bb5a`). */
/**
 * The caller's handle on an open Clarity dialog, and the reason `MatDialogRef` never leaves this
 * library.
 *
 * It **opens** the dialog rather than being handed one, and that is load-bearing: the body component
 * is instantiated inside `MatDialog.open`, so the ref it injects has to exist before that call. A ref
 * built from `open`'s return value would be one turn too late. Opening from the constructor means
 * `this` can be provided into the body's injector on the line above.
 *
 * Nothing here re-implements what Material already owns. The focus trap, the restore-to-opener, the
 * Escape handler and the backdrop press are all Material's, gated by its `disableClose`;
 * {@link setDismissible} is a live write to that flag, which is what lets a body lock dismissal for
 * the duration of an in-flight action and release it afterwards.
 *
 * The single exception is the backdrop, which a Clarity dialog refuses unless the caller passes
 * `backdropDismissible: true`. `disableClose` is one flag for two presses: refusing the backdrop means
 * pinning it on and taking the Escape back over (`closeMatDialogOnEscape`). The intent that gates it is the same live one, so `setDismissible` reads
 * no differently on such a dialog.
 *
 * A dialog opened with `focus: 'passive'` is not Material's at all: it is `ClarityPassiveDialog`,
 * brain's `Modal` on a bare CDK overlay, behind the same three members — so everything below, the
 * construction window included, holds for both.
 */
export class ClarityDialogRef<R = unknown> {
	/**
	 * Emits once — the body's result, or `undefined` for a dismissal — and completes.
	 *
	 * No `$` suffix: the member name is this library's agreed public contract and is shared verbatim
	 * with `ClarityFlyInRef`, so spelling it Finnish here would make the two primitives' handles
	 * read differently at every call site for no gain.
	 */
	// eslint-disable-next-line rxjs-x/finnish -- agreed public contract, see the doc comment above
	public readonly closed: Observable<R | undefined>;

	/**
	 * Undefined for the length of the construction window described on {@link dismissible} — Material
	 * builds the body inside `open()`, so anything the body does in its own constructor runs before
	 * this field is assigned. Stated in the type rather than left to a comment, because both members
	 * a body can reach in that window have to answer for it.
	 */
	private readonly dialogRef: ClarityDialogHandle<R> | undefined;

	/**
	 * Whether `disableClose` stays on even while the dialog is dismissible.
	 *
	 * True for every Material dialog except one whose caller granted the backdrop press. Material
	 * spends one flag on Escape and the backdrop together, so that refusal costs the Escape too, and
	 * `closeMatDialogOnEscape` gives it back. The passive backend separates the two itself, so nothing is
	 * pinned there and `disableClose` keeps its plain meaning on both.
	 */
	private readonly disableCloseIsPinned: boolean;

	/**
	 * A close that arrived during the construction window, replayed once the dialog exists.
	 *
	 * A wrapper rather than a bare result, so "closed with `undefined`" and "never closed" stay
	 * distinguishable — the dismissal case is the one that carries no result.
	 */
	private pendingClose: { readonly result: R | undefined } | undefined;

	/**
	 * The current intent, mirrored onto `MatDialogRef.disableClose`.
	 *
	 * Held separately because a body may call {@link setDismissible} from its own constructor, which
	 * Material runs *inside* `open()` — before {@link dialogRef} exists. The value is re-applied once
	 * the dialog is up, so an early call is honoured instead of silently dropped.
	 */
	private dismissible: boolean;

	/**
	 * `MatDialog` (or, for a `passive` dialog, the CDK `Overlay`) is resolved through `parentInjector`
	 * rather than taken as a fourth parameter — the injector already has to be here to parent the
	 * body's own, and one lookup keeps the signature inside the workspace's parameter budget without
	 * inventing an options object for three values.
	 */
	constructor(parentInjector: Injector, content: Type<unknown>, config?: ClarityDialogConfig) {
		const backdropDismissible = config?.backdropDismissible ?? DEFAULT_BACKDROP_DISMISSIBLE;

		this.dismissible = config?.dismissible ?? DEFAULT_DISMISSIBLE;
		this.disableCloseIsPinned = config?.focus !== 'passive' && !backdropDismissible;

		// `MatDialogConfig` has `injector`, not `providers`, so this hop is how the body is given the
		// ref and the data without ever seeing a Material symbol.
		const injector = Injector.create({
			parent: parentInjector,
			providers: [
				{ provide: ClarityDialogRef, useValue: this },
				{ provide: CLARITY_OVERLAY_DATA_TOKEN, useValue: config?.data },
			],
		});

		// Through a local as well as the field: inside this constructor the field's type still admits
		// `undefined`, and narrowing it on every line below would say the window is still open here.
		const dialogRef =
			config?.focus === 'passive'
				? new ClarityPassiveDialog<R>({
						parentInjector,
						content,
						contentInjector: injector,
						ariaLabel: config.ariaLabel,
						width: resolveWidth(config),
						maxWidth: resolveMaxWidth(config),
						maxHeight: resolveMaxHeight(config),
						panelClasses: resolvePanelClasses(config),
						backdropDismissible,
						closeOnNavigation: config.closeOnNavigation ?? DEFAULT_CLOSE_ON_NAVIGATION,
					})
				: this.openMaterialDialog(content, injector, config);

		this.dialogRef = dialogRef;
		this.applyDismissible();
		this.closed = dialogRef.afterClosed();

		// Last, and after `closed` exists: a body that closed from its constructor must still be
		// observable by the caller that is about to subscribe to the ref it is being handed.
		this.applyPendingClose(dialogRef);
	}

	/**
	 * Closes the dialog with a result. Works regardless of {@link setDismissible}.
	 *
	 * A call from the body's constructor lands in the construction window, where there is no
	 * `MatDialogRef` to close yet. It is held and replayed rather than thrown or dropped — the same
	 * answer {@link setDismissible} gives, because a body author reaches both members the same way.
	 */
	public close(result?: R): void {
		if (this.dialogRef === undefined) {
			this.pendingClose = { result };

			return;
		}

		this.dialogRef.close(result);
	}

	/**
	 * Suppresses or restores Escape and backdrop dismissal while the dialog is open.
	 *
	 * The in-flight lock: a body disables dismissal before a mutation and re-enables it when the
	 * mutation settles, so a half-finished action cannot be walked away from.
	 *
	 * It outranks `backdropDismissible`, never the other way round: a dialog that refuses the backdrop
	 * loses and regains only its Escape here, and `false` still revokes both.
	 */
	public setDismissible(dismissible: boolean): void {
		this.dismissible = dismissible;
		this.applyDismissible();
	}

	/**
	 * The default backend — Material's dialog, with the library's geometry and focus decisions.
	 * `MatDialog` resolves through `injector`, whose parent is the one the caller chose.
	 */
	private openMaterialDialog(
		content: Type<unknown>,
		injector: Injector,
		config: ClarityDialogConfig | undefined,
	): MatDialogRef<unknown, R> {
		const dialogRef = injector.get(MatDialog).open<unknown, never, R>(content, {
			injector,
			disableClose: this.isCloseDisabled(),
			ariaLabel: config?.ariaLabel,
			panelClass: resolvePanelClasses(config),
			width: resolveWidth(config),
			maxWidth: resolveMaxWidth(config),
			maxHeight: resolveMaxHeight(config),
			autoFocus: 'first-tabbable',
			restoreFocus: true,
			closeOnNavigation: config?.closeOnNavigation ?? DEFAULT_CLOSE_ON_NAVIGATION,
		});

		if (this.disableCloseIsPinned) {
			closeMatDialogOnEscape(dialogRef, () => this.dismissible);
		}

		return dialogRef;
	}

	private applyPendingClose(dialogRef: ClarityDialogHandle<R>): void {
		const pending = this.pendingClose;
		if (pending === undefined) {
			return;
		}

		this.pendingClose = undefined;
		dialogRef.close(pending.result);
	}

	private applyDismissible(): void {
		// Undefined only during the construction window described on `dismissible`.
		if (this.dialogRef !== undefined) {
			this.dialogRef.disableClose = this.isCloseDisabled();
		}
	}

	/**
	 * What both backends read as `disableClose`: the live dismissible intent, and the pin a
	 * backdrop-refusing Material dialog adds on top of it. Escape survives the pin through
	 * `closeMatDialogOnEscape`; nothing else does.
	 */
	private isCloseDisabled(): boolean {
		return !this.dismissible || this.disableCloseIsPinned;
	}
}
